import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ButtonGreen from "../../../components/ButtonGreen/ButtonGreen";
import ButtonWhite from "../../../components/ButtonWhite/ButtonWhite";
import ProductRepository from "../../../repositories/ProductRepository";
import { setBodyChat, setIsChooseProductOpen } from "../redux/Reducer";
import GeneralUtility from "../../../utils/general-utility";

const AttachProductModal = () => {
  const dispatch = useDispatch();
  const selectedItem = useSelector((state) => state.reducer.selectedItem);
  const IS_PRODUCTION = process.env.REACT_APP_IS_PRODUCTION;

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const containerRef = useRef(null);

  const loadMoreProducts = async () => {
    if (loading || !hasMore) return;

    setLoading(true);

    const payload = {
      page,
      size: 5,
      status: null,
      search: searchTerm,
      categoryId: null,
      sortBy: null,
      sortByDirection: null,
    };

    const res = await ProductRepository.getProduct(payload);
    const newData = res?.data ?? [];

    if (page === 0 && newData.length > 0) {
      setSelectedProduct(newData[0]);
    }

    setProducts((prev) => [...prev, ...newData]);
    setPage((prev) => prev + 1);
    if (newData.length < payload.size) setHasMore(false);

    setLoading(false);
  };

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - 50) {
      loadMoreProducts();
    }
  };

  useEffect(() => {
    // Saat pertama kali render atau saat searchTerm berubah
    setPage(0);
    setProducts([]);
    setHasMore(true);
  }, [searchTerm]);

  useEffect(() => {
    // Fetch pertama kali dan setiap kali products dikosongkan
    if (page === 0 && hasMore && !loading) {
      loadMoreProducts();
    }
  }, [products]);

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const handleSearch = debounce((value) => {
    setSearchTerm(value);
  }, 800);

  const handleSelectProduct = () => {
    const customerName = selectedItem?.customerName || "Teman Cuan";
    const fullLink = getUrl(selectedProduct);

    const bodyChat = `Hai Ka ${customerName}! Lihat deh produk ini, *${selectedProduct.productName}*, cocok banget buat kamu. \n \nLangsung cek ya di sini: \n${fullLink}`;

    dispatch(setBodyChat(bodyChat));
    dispatch(setIsChooseProductOpen(false));
  };

  const getUrl = (product) => {
    const productId = product.id;
    let baseUrl = "";
    if (IS_PRODUCTION === "false") {
      baseUrl += "http://merchant.tanamemas.co.id/apps";
    } else {
      baseUrl += "https://app-merchant.tanamemas.id/apps";
    }
    const queryParams = `?productId=${productId}&type=product`;
    return baseUrl + queryParams;
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={() => dispatch(setIsChooseProductOpen(false))}
        className="absolute inset-0 bg-[#1D2A26] opacity-50"
      ></div>

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[600px] rounded-lg bg-white py-4 pl-4 z-[50]">
        <h2 className="text-green-primary font-bold text-[28px] leading-[100%] mb-4">
          Attach Product
        </h2>

        {/* Search Input */}
        <label className="relative block mt-0 mb-4 pr-4">
          <span className="absolute inset-y-0 left-1.5 flex items-center pl-2">
            <img
              className="h-3.5 w-5"
              src={process.env.REACT_APP_ASSETS_IMAGE + "search-icon.svg"}
              alt="search-icon"
            />
          </span>
          <input
            type="text"
            placeholder={"Search Name"}
            className="pl-10 p-2 w-full block sm:text-sm shadow-sm border border-white rounded-xl bg-[#EDEDED] placeholder:text-slate-40"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </label>

        {/* Product List with Scroll */}
        <div
          className="h-[350px] overflow-y-auto pr-4"
          ref={containerRef}
          onScroll={handleScroll}
        >
          {products.map((item, index) => (
            <div
              key={index}
              onClick={() => setSelectedProduct(item)}
              className="flex flex-row gap-x-2 items-center w-full mt-4 hover:bg-slate-50 hover:rounded px-2 py-1 cursor-pointer"
            >
              <div className="w-[4.5em] h-[4.5em] border-2 border-gray-primary rounded-xl overflow-hidden">
                <img
                  className="w-full h-full object-cover"
                  src={item?.image}
                  alt=""
                />
              </div>

              <div>
                <div className="font-bold">{item.productName}</div>
                <div className="flex flex-row gap-x-1 items-center">
                  <p className="text-sm text-slate-600">
                    Rp{GeneralUtility.addCommas(item?.price ?? 0)}
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center gap-2 ml-auto">
                {selectedProduct?.id === item.id ? (
                  <div className="w-6 h-6 rounded-full bg-[#2C5752] flex items-center justify-center">
                    <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-[#D2D3D5]"></div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="text-center py-2 text-sm text-gray-400">
              Loading...
            </div>
          )}
        </div>

        {/* Footer Action */}
        <div className="flex items-center justify-end px-4 pt-4 gap-2">
          <ButtonWhite
            title={"Cancel"}
            onClick={() => dispatch(setIsChooseProductOpen(false))}
          />
          <ButtonGreen title={"Attach Product"} onClick={handleSelectProduct} />
        </div>
      </div>
    </>
  );
};

export default AttachProductModal;
